import { useMemo } from "react"
import { motion, AnimatePresence } from "motion/react"
import { evaluateHand, detectDraws } from "@/utils/pokerHandEvaluator"
import type { HandEvaluation, DrawResult } from "@/utils/pokerHandEvaluator"
import type { Card } from "@/lib/pokerEngine"

const FONT = "Outfit, -apple-system, BlinkMacSystemFont, sans-serif"

const getStrengthColor = (rank: number): string => {
  if (rank >= 7) return "#32CD32"   // Four of a Kind+ = green
  if (rank >= 5) return "#32CD32"   // Flush/Full House = green
  if (rank >= 3) return "#FFD700"   // Three of a Kind/Straight = gold
  if (rank >= 1) return "#F37020"   // Pair/Two Pair = orange
  return "#EF4444"                  // High Card = red
}

const getStrengthLabel = (rank: number): string => {
  if (rank >= 7) return "Monster"
  if (rank >= 5) return "Strong"
  if (rank >= 3) return "Good"
  if (rank >= 1) return "Marginal"
  return "Weak"
}

const getStrengthWidth = (rank: number): string => {
  return `${Math.min(100, Math.max(10, ((rank + 1) / 10) * 100))}%`
}

const HandStrengthTip = ({
  holeCards,
  communityCards,
}: {
  holeCards: Card[]
  communityCards: Card[]
}) => {
  const evaluation = useMemo<HandEvaluation>(
    () => evaluateHand(holeCards, communityCards),
    [holeCards, communityCards]
  )

  const draws = useMemo<DrawResult[]>(
    () => detectDraws(holeCards, communityCards),
    [holeCards, communityCards]
  )

  if (holeCards.length === 0) return null

  const strengthColor = getStrengthColor(evaluation.handRank)
  const strengthLabel = getStrengthLabel(evaluation.handRank)
  const barWidth = getStrengthWidth(evaluation.handRank)

  return (
    <motion.div
      className="absolute bottom-3 right-3 z-20 w-[180px] md:w-[200px]"
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.95 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      <div className="bg-[#1C1C32]/85 border border-[#2A2A45] rounded-xl backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.4)] overflow-hidden">
        {/* Header */}
        <div className="px-3 py-1.5 border-b border-[#2A2A45]/60">
          <p
            className="text-[10px] font-bold text-white/40 uppercase tracking-wider"
            style={{ fontFamily: FONT }}
          >
            Hand Strength
          </p>
        </div>

        {/* Hand Name */}
        <div className="px-3 pt-2 pb-1">
          <p
            className="text-sm font-black text-white leading-tight"
            style={{ fontFamily: FONT }}
          >
            {evaluation.handName}
          </p>
          <div className="flex items-center gap-2 mt-1.5">
            <span
              className="text-[10px] font-bold uppercase"
              style={{ fontFamily: FONT, color: strengthColor }}
            >
              {strengthLabel}
            </span>
          </div>
          {/* Strength Bar */}
          <div className="w-full h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: strengthColor }}
              initial={{ width: 0 }}
              animate={{ width: barWidth }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* Draws Section */}
        <AnimatePresence>
          {draws.length > 0 && (
            <motion.div
              className="px-3 pb-2 pt-1 border-t border-[#2A2A45]/40 mt-1"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <p
                className="text-[9px] font-bold text-white/30 uppercase tracking-wider mb-1"
                style={{ fontFamily: FONT }}
              >
                Draws
              </p>
              {draws.map((draw) => (
                <div
                  key={draw.drawName}
                  className="flex items-center justify-between py-0.5"
                >
                  <span
                    className="text-[11px] font-medium text-white/60"
                    style={{ fontFamily: FONT }}
                  >
                    {draw.drawName}
                  </span>
                  <span
                    className="text-[10px] font-bold text-[#FFD700]"
                    style={{ fontFamily: FONT }}
                  >
                    {draw.outs} out{draw.outs !== 1 ? "s" : ""}
                  </span>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

export default HandStrengthTip
